import LocalChatFilesManifest from '@/services/LocalChatFilesManifest'
import { useState } from 'react'
import { useEffect } from 'react'
import { logger } from '@/services/Logger'

const log = logger.createChildLogger({ name: 'useFileCacheStatus' })

// Custom hook to track if a file is in cache
export default function useFileCacheStatus(fileLink: string, filename: string) {
    const [fileDownloaded, setFileDownloaded] = useState<boolean>(false)
    const [refreshKey, setRefreshKey] = useState<number>(0)

    useEffect(() => {
        // Check if file is already in cache
        const checkCache = () => {
            const inCache = LocalChatFilesManifest.exists(fileLink)
            if (inCache && !fileDownloaded) {
                log.verbose('useFileCacheStatus', `File "${filename}" found in cache, updating status`)
                setFileDownloaded(true)
                setRefreshKey((prev: number) => prev + 1)
            }
            return inCache
        }

        checkCache()

        const handleFileAdded = (event: Event) => {
            const customEvent = event as CustomEvent
            const addedFileLink = customEvent.detail

            if (addedFileLink === fileLink && !fileDownloaded) {
                log.verbose('useFileCacheStatus', `File "${filename}" added to cache, updating status`)
                setFileDownloaded(true)
                setRefreshKey((prev: number) => prev + 1)
            }
        }

        const handleFileRemoved = (event: Event) => {
            const customEvent = event as CustomEvent
            const removedFileLink = customEvent.detail

            if (removedFileLink === fileLink && fileDownloaded) {
                log.verbose('useFileCacheStatus', `File "${filename}" removed from cache, updating status`)
                setFileDownloaded(false)
                setRefreshKey((prev: number) => prev + 1)
            }
        }

        LocalChatFilesManifest.addEventListener('fileAdded', handleFileAdded)
        LocalChatFilesManifest.addEventListener('fileRemoved', handleFileRemoved)

        return () => {
            LocalChatFilesManifest.removeEventListener('fileAdded', handleFileAdded)
            LocalChatFilesManifest.removeEventListener('fileRemoved', handleFileRemoved)
        }
    }, [fileLink, filename, fileDownloaded])

    return { fileDownloaded, setFileDownloaded, refreshKey, setRefreshKey }
}
